import TriangleIcon from "@lyteflo/assets/icons/triangle-filled.svg?react";
import {
  DEALER_CONTEXT_TYPE,
  type DealerContext,
  type DealerContextSection,
} from "@lyteflo/utils";

interface DealerContextDropdownMenuProps {
  sections: DealerContextSection[];
  selectedKey?: string;
  isLoading?: boolean;
  onOptionSelect: (next: DealerContext) => void;
  t: (key: string) => string;
  hasResults: boolean;
}

interface DealerContextOptionSectionProps {
  section: DealerContextSection;
  selectedKey?: string;
  onOptionSelect: (next: DealerContext) => void;
}

const isPinnedSection = (section: DealerContextSection) =>
  section.options.some((o) => o.value.type === DEALER_CONTEXT_TYPE.ALL);

const DealerContextOptionSection = ({
  section,
  selectedKey,
  onOptionSelect,
}: DealerContextOptionSectionProps) => (
  <div>
    {section.headerLabel && (
      <div className="lf-sticky lf-top-0 lf-z-10 lf-border-b lf-border-[var(--gray-4)] lf-bg-panel-default lf-px-3 lf-pt-2 lf-pb-1 lf-text-[11px] lf-font-semibold lf-tracking-wide lf-text-[var(--gray-11)] lf-uppercase">
        {section.headerLabel}
      </div>
    )}
    {section.options.map((option) => {
      const contextKey =
        option.value.type === DEALER_CONTEXT_TYPE.ALL
          ? DEALER_CONTEXT_TYPE.ALL
          : option.value.id;
      const isGroupRow = option.value.type === DEALER_CONTEXT_TYPE.GROUP;
      const isSelected = selectedKey === contextKey;

      const indent = option.indent ? "lf-pl-9 lf-pr-3" : "lf-px-3";
      const activeClasses = isSelected
        ? "lf-bg-[var(--accent-8)] lf-text-[var(--accent-contrast)]"
        : "";

      return (
        <button
          key={option.key}
          type="button"
          className={`lf-flex lf-w-full lf-items-center ${indent} lf-py-1.5 lf-text-left hover:lf-bg-[var(--accent-9)] hover:lf-text-[var(--accent-contrast)] ${activeClasses}`}
          onClick={() => onOptionSelect(option.value)}
          data-context-key={contextKey}
        >
          {isGroupRow && (
            <TriangleIcon className="lf-mr-2 lf-size-3 lf-shrink-0 lf-rotate-90" />
          )}
          <span>{option.label}</span>
        </button>
      );
    })}
  </div>
);

export const DealerContextDropdownMenu = ({
  sections,
  selectedKey,
  isLoading,
  onOptionSelect,
  t,
  hasResults,
}: DealerContextDropdownMenuProps) => {
  if (isLoading) {
    return (
      <div className="lf-px-3 lf-py-2 lf-text-[var(--gray-9)]">
        {t("shared:loading")}
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className="lf-px-3 lf-py-2 lf-text-[var(--gray-9)]">
        {t("shared:noResults")}
      </div>
    );
  }

  const pinnedSections = sections.filter(isPinnedSection);
  const scrollableSections = sections.filter((s) => !isPinnedSection(s));

  return (
    <>
      {pinnedSections.length > 0 && (
        <div
          className={
            scrollableSections.length > 0
              ? "lf-border-b lf-border-[var(--gray-4)]"
              : ""
          }
        >
          {pinnedSections.map((section) => (
            <DealerContextOptionSection
              key={section.key}
              section={section}
              selectedKey={selectedKey}
              onOptionSelect={onOptionSelect}
            />
          ))}
        </div>
      )}
      {scrollableSections.length > 0 && (
        <div
          className="lf-max-h-80 lf-overflow-y-auto"
          data-testid="dealer-context-scroll-area"
        >
          {scrollableSections.map((section) => (
            <DealerContextOptionSection
              key={section.key}
              section={section}
              selectedKey={selectedKey}
              onOptionSelect={onOptionSelect}
            />
          ))}
        </div>
      )}
    </>
  );
};
